<script setup lang="ts">
import { MutationTypes, type State } from '@/store/store_types'
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import { ErrorType } from '../../store/store_types'
import ElevatedBtn from '../ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBtn from '../ui-elements/buttons/OutlinedBtn.vue'

interface ErrorData {
  title: string
  message: string
  actionName: string
  action: () => void
  displayDoNotShowAgainInput: boolean
}

const refresh = () => {
  window.location.reload()
}

const fallbackErrorData: ErrorData = {
  title: 'An unexpected error has occurred',
  message: 'We are sorry for the inconvenience. Please try again later.',
  actionName: 'Refresh',
  action: () => refresh(),
  displayDoNotShowAgainInput: false,
}

const errorList: Record<ErrorType, ErrorData> = {
  [ErrorType.FETCHING_ERROR]: {
    title: 'Error fetching data from our servers',
    message: 'Check your internet connection and try again, or try again later.',
    actionName: 'Refresh',
    action: () => refresh(),
    displayDoNotShowAgainInput: false,
  },
}

const store = useStore<State>()
const errorToDisplay = computed(() => store.state.errorToDisplay)
const activator = computed(() => !!errorToDisplay.value)
const errorData = computed(
  () => (errorToDisplay.value && errorList[errorToDisplay.value]) || fallbackErrorData,
)
const title = computed(() => errorData.value?.title)
const message = computed(() => errorData.value?.message)
const actionName = computed(() => errorData.value?.actionName)

const close = () => {
  store.commit(MutationTypes.setErrorToDisplay, undefined)
}

const action = () => {
  errorData.value?.action()
  close()
}

const displayDoNotShowAgainInput = computed(() => errorData.value?.displayDoNotShowAgainInput)
const handleDoNotShowAgain = () => {}
const handleShowAgain = () => {}

const { mobile } = useDisplay()
</script>

<template>
  <v-dialog
    :model-value="activator"
    :width="mobile ? undefined : '70%'"
    :max-width="mobile ? undefined : '700px'"
    :content-class="mobile ? '' : 'rounded-lg'"
    @update:modelValue="
      (val) => {
        if (!val) close()
      }
    "
    style="z-index: 2000"
  >
    <v-card :rounded="mobile ? '0' : 'lg'" class="pa-4 pb-10">
      <v-row no-gutters justify="end">
        <v-btn rounded="lg" size="small" color="pwt_off_white" flat>
          <v-icon color="pwt_light_grey" @click="close">mdi-close</v-icon>
        </v-btn>
      </v-row>
      <v-container class="px-5 py-2 d-flex flex-column align-center">
        <v-card flat max-width="70%">
          <v-card-title
            class="d-flex flex-column align-center text-center pt-0 pb-8 font-weight-bold"
          >
            <v-icon color="pwt_red" size="50">mdi-alert-octagon-outline</v-icon>
            <span>{{ title }} </span>
          </v-card-title>
          <v-card-text class="text-center">
            {{ message }}
          </v-card-text>
          <v-card-actions class="justify-center pt-5">
            <OutlinedBtn @click="close" class="mr-2" color="pwt_red"> Dismiss </OutlinedBtn>
            <ElevatedBtn @click="action" class="ml-2"> {{ actionName }} </ElevatedBtn>
          </v-card-actions>
        </v-card>
      </v-container>
      <v-row v-if="displayDoNotShowAgainInput" no-gutters justify="start" class="mt-10 px-5">
        <v-checkbox
          color="pwt_palatinate_blue"
          base-color="pwt_palatinate_blue"
          hide-details
          density="compact"
          @update:modelValue="
            (val) => {
              if (val) handleDoNotShowAgain()
              else handleShowAgain()
            }
          "
        >
          <template v-slot:label>
            <span class="text-pwt_palatinate_blue text-decoration-underline text-caption">
              Don't show again
            </span>
          </template>
        </v-checkbox>
      </v-row>
    </v-card>
  </v-dialog>
</template>
